import { Card, BlockStack, Text, Checkbox, Banner, InlineStack, Badge } from '@shopify/polaris';
import { useCallback } from 'react';
import { StaggeredList, AccentBorderCard } from '../ui';

/**
 * T021: ScanSetupStep — Step 4 of the onboarding wizard.
 * Scope display (25 products), email toggle (default OFF), and Begin scan button.
 */
interface ScanSetupStepProps {
  emailNotify: boolean;
  onEmailNotifyChange: (checked: boolean) => void;
  onBeginScan: () => void;
  onBack: () => void;
  isLoading: boolean;
  productLimit: number;
}

export function ScanSetupStep({
  emailNotify,
  onEmailNotifyChange,
  onBeginScan: _onBeginScan,
  onBack: _onBack,
  isLoading: _isLoading,
  productLimit,
}: Readonly<ScanSetupStepProps>) {
  const handleEmailChange = useCallback(
    (checked: boolean) => {
      onEmailNotifyChange(checked);
    },
    [onEmailNotifyChange],
  );

  return (
    <StaggeredList staggerDelay={0.06}>
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingLg">
            Ready to scan
          </Text>

          <Text as="p" variant="bodyMd">
            Zelium will now scan a sample of your products and generate your AI Discoverability
            Score. This usually takes 2–5 minutes.
          </Text>

          <AccentBorderCard borderColor="var(--z-accent)">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  Scan scope
                </Text>
                <Badge tone="info">Free plan</Badge>
              </InlineStack>

              <Text as="p" variant="bodyMd">
                Up to <Text as="span" fontWeight="bold">{productLimit} products</Text> (most recently
                updated)
              </Text>

              <Text as="p" variant="bodySm" tone="subdued">
                Upgrade later to scan your entire catalog.
              </Text>
            </BlockStack>
          </AccentBorderCard>

          <Checkbox
            label="Email me when the scan is complete"
            helpText="We'll send a one-time notification with your score summary."
            checked={emailNotify}
            onChange={handleEmailChange}
          />
        </BlockStack>
      </Card>

      <Banner tone="info">
        <p>
          The scan runs in the background — you can close the app and come back later.
        </p>
      </Banner>
    </StaggeredList>
  );
}
